"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-foreground transition-colors hover:text-ring focus:text-ring outline-none disabled:pointer-events-none disabled:opacity-50 relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-[1px] after:w-0 after:bg-ring after:transition-all hover:after:w-[calc(100%-2rem)]">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-lg font-medium text-foreground hover:text-ring transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="226908" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/">
            <WvcLogo className="w-auto !bg-transparent !h-[60px] rounded-md [&_svg]:w-auto [&_svg]:!bg-transparent [&_svg]:!h-[60px] [&_svg]:rounded-md" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center justify-end gap-4">
          <Button asChild className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-default font-semibold transition-all shadow-md px-8">
            <Link to="https://yswellness.noterro.com">Book Now</Link>
          </Button>

          {}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="mt-6">
                <Link to="/" className="inline-block mb-8">
                  <WvcLogo className="h-10 w-auto" />
                </Link>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}